import React, { useEffect, useState } from "react";
import { dateToString } from "~/utils/formatter";
import { API_STATE } from "~/common/constants/state";
import useRequest from "~/common/hooks/useRequest";
import { Trans, useTranslation } from "react-i18next";

// Components
import NavbarAndSidebarLayout from "~/layouts/NavbarAndSidebarLayout";
import PageHeader from "~/common/components/PageHeader";
import ModalTemplate from "~/common/components/ModalPortal/ModalTemplate";
import InvitationModal from "./modal/InvitationModal";
import CompleteModal from "./modal/CompleteModal";
import MemberList from "./contents/MemberList";

// Assets
import "~/assets/sass/pages/mypage/admin/team.scss";
import { TeamPageIcon } from "~/assets/svg/PageIntroIcon";
import useAlert from "~/common/hooks/useAlert";
import { useForm } from "react-hook-form";
import ResendMailModal from "./modal/ResendMailModal";

const AdminTeamPage = () => {
	const { t } = useTranslation();
	const MENU = ["admin", "team"];
	const TYPE = "myPage";

	const headerButtons = [
		<button
			type="button"
			className="btn__solid"
			color="primary"
			onClick={() => setInviteModal(true)}
		>
			{t("common.commonWord.button.invite")}
		</button>,
	];

	const [inviteModal, setInviteModal] = useState(false);
	const [resendMember, setResendMember] = useState();
	const [teamInviteRequestModal, setTeamInviteRequestModal] = useState(false);
	const [resendMailModal, setResendMailModal] = useState(false);
	const [teamMemberList, setTeamMemberList] = useState([]);
	const [inviteResponseData, setInviteResponseData] = useState([]);

	const [showWithdrawAlert, WithdrawAlert] = useAlert();
	const [showSuccessAlert, SuccessAlert] = useAlert();

	const invitedTeamMemberRequest = useRequest("get", "/vc/admin/invite");
	const activationRequest = useRequest("post", "/vc/admin/activate");
	const deleteRequest = useRequest("post", "/vc/admin/withdraw");
	const resendRequest = useRequest("post", `/vc/admin/invite/resend/${resendMember?.idx}`);

	const resendMailForm = useForm();

	const submit = ({ password }) => {
		handleResend(password);
	};

	useEffect(() => {
		invitedTeamMemberRequest.singleCall();
	});

	useEffect(() => {
		switch (invitedTeamMemberRequest.state) {
			case API_STATE.done:
				const newData = invitedTeamMemberRequest.response.data.map((d) => ({
					...d,
					regDate: dateToString(d.regDate, "yyyy-MM-dd"),
				}));
				setTeamMemberList(newData);
				break;
			case API_STATE.error:
				console.log("error", invitedTeamMemberRequest.error);
				break;
			default:
				break;
		}
	}, [
		invitedTeamMemberRequest.state,
		invitedTeamMemberRequest.response,
		invitedTeamMemberRequest.error,
	]);

	// Active UnActive
	const handleActive = (idx) => {
		activationRequest.call(idx, "path");
	};

	useEffect(() => {
		switch (activationRequest.state) {
			case API_STATE.done:
				invitedTeamMemberRequest.call();
				break;
			case API_STATE.error:
				console.log("error", activationRequest.error);
				alert(t("common.commonWord.alert.wait2"));
				break;
			default:
				break;
		}
	}, [activationRequest.state, activationRequest.response, activationRequest.error]);

	// Delete
	const handleDelete = async (idx) => {
		showWithdrawAlert()
			.then(() => deleteRequest.call(idx, "path"))
			.catch(() => {});
	};

	useEffect(() => {
		switch (deleteRequest.state) {
			case API_STATE.done:
				invitedTeamMemberRequest.call();
				break;
			case API_STATE.error:
				console.log("error", deleteRequest.error);
				alert(t("common.commonWord.alert.wait2"));
				break;
			default:
				break;
		}
	}, [deleteRequest.state, deleteRequest.response, deleteRequest.error]);

	// Mail sender
	const handleResend = (password) => {
		showSuccessAlert()
			.then(() => {
				resendRequest.call({ password: password }, "body");
				setResendMailModal();
			})
			.catch(() => {
				setResendMailModal();
			});
	};

	return (
		<>
			<NavbarAndSidebarLayout menu={MENU} type={TYPE}>
				<PageHeader buttonList={headerButtons} />
				<div className="pageIntro">
					<TeamPageIcon />
					<div className="content">
						<h3>
							<Trans
								i18nKey="pages.myPage.admin.TeamPage.content.h3"
								components={[<b></b>]}
							/>
						</h3>
						<p className="pre">{t("pages.myPage.admin.TeamPage.content.p")}</p>
					</div>
				</div>
				<MemberList
					{...{
						teamMemberList,
						handleActive,
						handleDelete,
						setResendMailModal,
						setResendMember,
					}}
				/>
			</NavbarAndSidebarLayout>

			{/*추가하기 버튼 클릭시 팀원초대 모달*/}
			<ModalTemplate
				modalState={inviteModal}
				setModalToggle={setInviteModal}
				title={t("pages.myPage.admin.TeamPage.inviteModalTitle")}
				width={700}
				height={490}
				className={"modal-team-invite"}
			>
				<InvitationModal
					{...{
						setTeamInviteRequestModal,
						setInviteModal,
						invitedTeamMemberRequest,
						setInviteResponseData,
					}}
				/>
			</ModalTemplate>
			{/* 이메일 전송 완료 */}
			<ModalTemplate
				modalState={teamInviteRequestModal}
				setModalToggle={setTeamInviteRequestModal}
				title={t("pages.myPage.admin.TeamPage.inviteModalTitle")}
				width={550}
				height={447}
				className={"modal-team-invite"}
			>
				<CompleteModal {...{ inviteResponseData, invitedTeamMemberRequest }} />
			</ModalTemplate>
			{/* 초대 메일 재전송 모달 */}
			<ModalTemplate
				title="초대 메일 재전송"
				width={700}
				height={343}
				modalState={resendMailModal}
				setModalToggle={setResendMailModal}
				className="modal-resend-mail"
				// closeable={true}
			>
				<ResendMailModal
					{...{ resendMailForm, resendMember, setResendMailModal, submit }}
				/>
			</ModalTemplate>
			<SuccessAlert
				message={
					<>
						<h3 className="main-desc">팀원에게 초대 메일을 재전송합니다.</h3>
						<p className="sub-desc">
							비밀번호 변경 후 초대 메일을 재전송할 경우
							<br /> 기존 비밀번호로는 로그인이 불가합니다.
						</p>
					</>
				}
				className="modal-resend-success"
				confirm="확인"
				reject="취소"
			/>
			{/* 팀원 삭제 alert */}
			<WithdrawAlert
				type="warning"
				message={t("pages.myPage.admin.TeamPage.WithdrawAlert.title")}
				confirm={t("common.commonWord.button.yes")}
				reject={t("common.commonWord.button.no")}
			/>
		</>
	);
};

export default AdminTeamPage;
